import * as d3 from 'd3';
import type { Location } from '../domain/Location';
import type { MigrationArc } from '../domain/MigrationArc';
import type { TimelinePointKind } from '../domain/TimelinePoint';
import { AppStore } from '../store/AppStore';
import { TimelineStore } from '../store/TimelineStore';
import { SelectionStore } from '../store/SelectionStore';
import { Component } from './Component';
import type { MapView } from './MapView';
import { computeGenerations, filterVisibleArcs } from './ArcLayer';
import { TOOLTIP_STYLE } from './DotLayer';
import { anchorFor } from '../utils/CellKey';
import { createLogger } from '../utils/logger';
import { personDisplayName } from '../utils/PersonName';

const log = createLogger('WaypointLayer');

/**
 * A line end that no dot marks (P-15f): a sibling's or child's birth (evidence is not
 * dot mass), or an ancestor's own event with no year (dots need a year).
 */
export interface Waypoint {
  key: string;
  lat: number;
  lng: number;
  branchId: string;
  placeName: string;
  year?: number;
  personName: string;
  kind: TimelinePointKind;
  approximate: boolean;
  unverified: boolean;
}

const isEvidenceKind = (k?: TimelinePointKind): boolean =>
  k === 'sibling birth' || k === 'child birth';

/**
 * Small hollow markers at visible line ends that no dot marks: a sibling's or child's
 * birth at either end of a line (evidence is not dot mass — PLAN.md), and an
 * ancestor's own event with no year (dots need a year). Without a marker the line
 * would bend at, or start from, an empty point. Mounted between ArcLayer and
 * DotLayer, so a dot at the same place covers its waypoint.
 */
export class WaypointLayer extends Component {
  private group!: d3.Selection<SVGGElement, unknown, null, undefined>;
  private tooltip!: HTMLDivElement;

  constructor(private readonly mapView: MapView) {
    super();
  }

  render(): HTMLElement {
    // Renders into the MapView SVG group, not an HTML element.
    return document.createElement('span');
  }

  mount(_parent: HTMLElement): void {
    this.group = d3
      .select(this.mapView.getLayerGroup())
      .append('g')
      .attr('class', 'waypoint-layer')
      .attr('data-testid', 'waypoint-layer')
      .attr('aria-hidden', 'true'); // decorative SVG — accessible data in BranchPanel

    this.tooltip = document.createElement('div');
    this.tooltip.setAttribute('data-testid', 'waypoint-tooltip');
    this.tooltip.style.cssText = TOOLTIP_STYLE;
    document.body.appendChild(this.tooltip);

    this.track(AppStore.subscribe(() => this._redraw()));
    this.track(TimelineStore.subscribe(() => this._redraw()));
    this.track(SelectionStore.subscribe(() => this._redraw()));
    this.track(this.mapView.onViewChanged(() => this._reproject()));

    this._redraw();
    log.info('WaypointLayer mounted');
  }

  unmount(): void {
    this.group?.remove();
    this.tooltip?.remove();
    super.unmount();
  }

  private _reproject(): void {
    this.group
      .selectAll<SVGCircleElement, Waypoint>('circle.waypoint')
      .attr('cx', (d) => this.mapView.projectPoint(d.lat, d.lng).x)
      .attr('cy', (d) => this.mapView.projectPoint(d.lat, d.lng).y);
  }

  private _redraw(): void {
    const { arcs, branchPaths, graph, dotMap } = AppStore.getState();
    const { currentDecade } = TimelineStore.getState();
    const { generationCap, activeBranchId } = SelectionStore.getState();

    const colorMap = new Map(branchPaths.map((p) => [p.branchId, p.branchColor]));
    const gens = graph ? computeGenerations(graph) : new Map<string, number>();
    // Drawn at the cell's dot anchor, where the line ends (P-15f)
    const waypoints = buildWaypoints(
      filterVisibleArcs(arcs, currentDecade, generationCap, gens),
    ).map((w) => ({ ...w, ...anchorFor(dotMap, w.lat, w.lng) }));
    const opacity = (d: Waypoint) =>
      activeBranchId === null || d.branchId === activeBranchId ? 0.9 : 0.15;

    const join = this.group
      .selectAll<SVGCircleElement, Waypoint>('circle.waypoint')
      .data(waypoints, (d) => d.key);

    join.exit().remove();
    join
      .attr('stroke-opacity', opacity)
      .attr('cx', (d) => this.mapView.projectPoint(d.lat, d.lng).x)
      .attr('cy', (d) => this.mapView.projectPoint(d.lat, d.lng).y);
    join
      .enter()
      .append('circle')
      .attr('class', 'waypoint')
      .attr('r', 4)
      .attr('fill', '#ffffff')
      .attr('stroke', (d) => colorMap.get(d.branchId) ?? '#999999')
      .attr('stroke-width', 2)
      .attr('stroke-opacity', opacity)
      .attr('pointer-events', 'auto')
      .attr('cx', (d) => this.mapView.projectPoint(d.lat, d.lng).x)
      .attr('cy', (d) => this.mapView.projectPoint(d.lat, d.lng).y)
      .on('mouseenter', (event: MouseEvent, d: Waypoint) => this._showTooltip(event, d))
      .on('mouseleave', () => {
        this.tooltip.style.display = 'none';
      });

    log.debug(`WaypointLayer: ${waypoints.length} waypoints at decade ${currentDecade}`);
  }

  private _showTooltip(event: MouseEvent, d: Waypoint): void {
    this.tooltip.innerHTML = '';
    const lines = waypointLines(d);
    const heading = document.createElement('strong');
    heading.textContent = lines[0] ?? ''; // GEDCOM text — textContent only
    this.tooltip.appendChild(heading);
    for (const text of lines.slice(1)) {
      const line = document.createElement('div');
      line.textContent = text;
      this.tooltip.appendChild(line);
    }
    this.tooltip.style.display = 'block';
    this.tooltip.style.left = `${event.clientX + 14}px`;
    this.tooltip.style.top = `${event.clientY - 14}px`;
  }
}

// ── Module-level pure helpers (exported for unit tests) ──────────────────────

/**
 * One waypoint per ancestor, place and kind at each end of a visible arc that no dot
 * marks: a sibling or child birth (either end — a line can start at one when the
 * ancestor's birth has no place), or an ancestor's own event with no year. Pure — no
 * DOM. Exported for unit tests.
 */
export function buildWaypoints(visibleArcs: MigrationArc[]): Waypoint[] {
  const out = new Map<string, Waypoint>();
  for (const arc of visibleArcs) {
    const ends: Array<[Location, TimelinePointKind | undefined, number | undefined]> = [
      [arc.from, arc.fromKind, arc.fromYear],
      [arc.to, arc.toKind, arc.toYear],
    ];
    for (const [loc, kind, year] of ends) {
      if (kind === undefined || (!isEvidenceKind(kind) && year !== undefined)) {
        continue; // a dated ancestor event has its own dot
      }
      if (loc.lat === undefined || loc.lng === undefined) {
        continue;
      }
      const key = `${arc.person.id}|${loc.raw}|${kind}`;
      if (!out.has(key)) {
        out.set(key, {
          key,
          lat: loc.lat,
          lng: loc.lng,
          branchId: arc.branchId,
          placeName: loc.raw,
          ...(year !== undefined ? { year } : {}),
          personName: personDisplayName(arc.person),
          kind,
          approximate: loc.approximate === true,
          unverified: loc.unverified === true,
        });
      }
    }
  }
  return [...out.values()];
}

const WAYPOINT_EVENT: Record<TimelinePointKind, string> = {
  'sibling birth': 'sibling born here',
  'child birth': 'child born here',
  birth: 'born here',
  marriage: 'married here',
  'ancestor child birth': 'child born here',
};

/** Tooltip text: place heading, year (or "No date"), whose event, and location notes. */
export function waypointLines(d: Waypoint): string[] {
  const lines = [
    d.placeName,
    d.year !== undefined ? d.year.toString() : 'No date',
    `${d.personName} — ${WAYPOINT_EVENT[d.kind]}`,
  ];
  if (d.approximate) {
    lines.push('Approximate location');
  }
  if (d.unverified) {
    lines.push('Unverified location — place name has no state or country');
  }
  return lines;
}
